/**
 * Optional axios integration.
 *
 * axios uses XHR (browser) under the hood, so the XHR interceptor already sees axios
 * traffic. Attaching this gives *richer* context (axios config, response metadata) and
 * sets a marker header so the XHR interceptor skips the duplicate.
 *
 * Usage:
 *   import axios from 'axios';
 *   import { attachAxios } from '@aris/request-watcher-react';
 *   attachAxios(axios);
 */
import { getRuntime } from '../capture.js';
import { newSpan, traceHeaders, type SpanIds } from '../trace.js';
import { now, safeParseHeaders } from '../util.js';

export const AXIOS_MARKER = 'x-aris-axios';

// Minimal structural typing so we don't hard-depend on axios types.
interface AxiosLike {
  interceptors: {
    request: { use: (onFulfilled: (cfg: any) => any) => number; eject: (id: number) => void };
    response: {
      use: (onFulfilled: (res: any) => any, onRejected: (err: any) => any) => number;
      eject: (id: number) => void;
    };
  };
}

interface Meta {
  start: number;
  stack?: string;
  span: SpanIds;
}

export function attachAxios(instance: AxiosLike): () => void {
  const reqId = instance.interceptors.request.use((cfg: any) => {
    const span = newSpan();
    const meta: Meta = { start: now(), stack: new Error().stack, span };
    cfg.__aris = meta;
    cfg.headers = cfg.headers ?? {};
    cfg.headers[AXIOS_MARKER] = '1';
    for (const [k, v] of Object.entries(traceHeaders(span, 'unknown'))) cfg.headers[k] = v;

    // Inline firewall: reject before axios dispatches the request.
    const rt = getRuntime();
    const url: string = cfg.url ?? '';
    const full = cfg.baseURL && !/^https?:/i.test(url) ? `${cfg.baseURL}${url}` : url;
    if (rt && !rt.shouldIgnore(full)) {
      const policy = rt.enforce(full);
      if (policy.blocked) {
        const msg = `Blocked by Aris firewall (${policy.reason}): ${full}`;
        rt.capture({
          method: (cfg.method ?? 'get').toUpperCase(), url: full, status: null, ok: false,
          durationMs: 0, error: { type: 'ArisFirewallBlock', message: msg },
          stack: meta.stack, span, blocked: true,
        });
        throw new Error(msg);
      }
    }
    return cfg;
  });

  const resId = instance.interceptors.response.use(
    (res: any) => {
      record(res.config, res.status, res.headers, null);
      return res;
    },
    (err: any) => {
      const cfg = err?.config;
      const status = err?.response?.status ?? null;
      record(cfg, status, err?.response?.headers, {
        type: err?.code ?? 'AxiosError',
        message: err?.message,
      });
      return Promise.reject(err);
    },
  );

  return () => {
    instance.interceptors.request.eject(reqId);
    instance.interceptors.response.eject(resId);
  };
}

function record(
  cfg: any,
  status: number | null,
  respHeaders: any,
  error: { type?: string; message?: string } | null,
): void {
  const rt = getRuntime();
  if (!rt || !cfg) return;
  const meta: Meta | undefined = cfg.__aris;
  const url: string = cfg.url ?? '';
  const full = cfg.baseURL && !/^https?:/i.test(url) ? `${cfg.baseURL}${url}` : url;
  if (rt.shouldIgnore(full)) return;

  rt.capture({
    method: (cfg.method ?? 'get').toUpperCase(),
    url: full,
    status,
    ok: status !== null && status >= 200 && status < 400,
    durationMs: meta ? now() - meta.start : 0,
    requestHeaders: safeParseHeaders(cfg.headers),
    responseHeaders: respHeaders ? safeParseHeaders(respHeaders) : undefined,
    requestBodySize: typeof cfg.data === 'string' ? cfg.data.length : null,
    error,
    stack: meta?.stack,
    span: meta?.span ?? newSpan(),
  });
}
